import React, { useContext } from 'react';
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import Loader from "../components/atoms/Loader";
import { setAuthToken, setTokenRefreshHandler, refreshAuthToken } from "@api";
import { ConfigurationContext } from "../utils/ConfigurationProvider.jsx";

const REFRESH_THRESHOLD_MS = 300000; // refresh once within 5 minutes of expiry

export const TokenGuard = ({ component: Component }) => {
  const {getAccessTokenSilently, loginWithRedirect} = useAuth0();
  const {config} = useContext(ConfigurationContext);
  const [ready, setReady] = React.useState(false);
  const tokenExpiryRef = React.useRef(null);

  // Fetch a fresh access token, install it on the API client, and remember its expiry. Returns the
  // token so the API layer's 401 handler can use it to replay a request that failed on a lapsed token.
  const acquireToken = React.useCallback(async () => {
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: config.authAudience,
        scope: "access",
      },
    });

    const {exp} = JSON.parse(atob(token.split('.')[1])); // Decode JWT token to extract expiration time
    tokenExpiryRef.current = exp * 1000; // Convert to milliseconds
    setAuthToken(token);
    return token;
  }, [getAccessTokenSilently, config.authAudience]);

  React.useEffect(() => {
    let cancelled = false;

    // Let the API client's 401 handler refresh on demand and retry the failed request.
    setTokenRefreshHandler(acquireToken);

    const init = async () => {
      try {
        await acquireToken();
        if (!cancelled) setReady(true);
      } catch (error) {
        console.error("Error getting the access token", error);
        loginWithRedirect();
      }
    };

    init();

    const refreshIfExpiring = async () => {
      const expiry = tokenExpiryRef.current;
      if (expiry && expiry - Date.now() >= REFRESH_THRESHOLD_MS) return; // still comfortably valid
      try {
        await refreshAuthToken();
      } catch (error) {
        console.error("Error refreshing the access token", error);
        loginWithRedirect();
      }
    };

    const intervalId = setInterval(refreshIfExpiring, REFRESH_THRESHOLD_MS);

    // Timers are paused/throttled while the laptop sleeps or the tab is backgrounded, so the interval
    // above can sail past an expiry. Re-check the moment the tab is shown again — before react-query's
    // focus refetch fires — so the calendar never refetches with a dead token.
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshIfExpiring();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      setTokenRefreshHandler(null);
    };

  }, [acquireToken, loginWithRedirect]);

  return ready ? <Component/> : <span></span>;
}

export const AuthGuard = ({ component }) => {
  const Component = withAuthenticationRequired(component, {
    onRedirecting: () => (
      <Loader />
    ),
  });

  return <Component />;
};
